import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelTemplate } from "@/components/common/Modals"
import { DEFAULT_AUTOCOMPLETE_VALUE } from "@/constants/forms"
import type { Classification } from "@/graphql/codegen/graphql"
import AddProductClassForm from "@/screens/ManageProducts/components/ProductClasses/forms/AddProductClassForm"
import type { ProductClassificationFormValues } from "@/screens/ManageProducts/components/ProductClasses/forms/classSchema"
import { ProductClassificationSchema } from "@/screens/ManageProducts/components/ProductClasses/forms/classSchema"
import EditProductClassForm from "@/screens/ManageProducts/components/ProductClasses/forms/EditProductClassForm"
import useProductClassificationActions from "@/screens/ManageProducts/components/ProductClasses/hooks/useProductClassificationActions"
import type { DialogMode, EditDialogMode } from "@/screens/ManageProducts/components/ProductClasses/types"
import { DialogModes } from "@/screens/ManageProducts/components/ProductClasses/types"
import {
  prepareFormData,
  preprocessCreateInput,
  preprocessUpdateInput,
} from "@/screens/ManageProducts/components/ProductClasses/utils"

interface ClassFormDialogProps {
  onSuccess?: () => void
  onClose: () => void
  mode?: DialogMode
  data?: Classification
}

const defaultFormValues: ProductClassificationFormValues = {
  category: DEFAULT_AUTOCOMPLETE_VALUE,
  name: "",
  productManager: DEFAULT_AUTOCOMPLETE_VALUE,
}

const ClassFormDialog = ({
  onClose,
  onSuccess,
  mode = DialogModes.Create,
  data,
}: ClassFormDialogProps): ReactElement => {
  const isCreateMode = mode === DialogModes.Create

  const schema =
    mode === DialogModes.EditName
      ? ProductClassificationSchema.omit({ productManager: true })
      : ProductClassificationSchema

  const methods = useForm<ProductClassificationFormValues>({
    defaultValues: isCreateMode ? defaultFormValues : prepareFormData(data as Classification),
    resolver: zodResolver(schema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    setError,
    formState: { isValid, dirtyFields, isDirty },
  } = methods

  const { createProductClassification, updateProductClassification } = useProductClassificationActions({
    setError,
    onSuccess,
    onClose,
  })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const onSubmit = handleSubmit(async (input: ProductClassificationFormValues) => {
    if (isCreateMode) {
      const processedInput = preprocessCreateInput(input)
      await createProductClassification(processedInput)
    } else {
      const processedInput = preprocessUpdateInput(input, (data as Classification).classificationId, dirtyFields)
      await updateProductClassification(processedInput, dirtyFields)
    }
  })

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  let dialogTitle

  if (isCreateMode) {
    dialogTitle = "New Product Class"
  } else if (mode === DialogModes.EditProductManager) {
    dialogTitle = "Change Product Manager"
  } else {
    dialogTitle = "Edit Class Name"
  }

  const canSubmit = isValid && isDirty

  return (
    <Dialog open={true} onClose={handleCancel} maxWidth='sm' fullWidth data-testid='class-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelTemplate onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>{dialogTitle}</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              {isCreateMode ? (
                <AddProductClassForm onSubmit={onSubmit} />
              ) : (
                <EditProductClassForm onSubmit={onSubmit} mode={mode as EditDialogMode} />
              )}
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              type='submit'
              disabled={!canSubmit}
              onClick={onSubmit}
              data-testid='submit-button'
            >
              {isCreateMode ? "Save" : "Update"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default ClassFormDialog
